// app.routes.ts

import { Routes } from '@angular/router';
import { publicGuard } from './core/guards/public.guard';
import { authGuard } from './core/guards/auth.guard';
import { unsavedChangesGuard } from './core/guards/pending-changes.guard'
import { reportIncidentUploadGuard } from './core/guards/report-incident-upload.guard';
import { environment } from '@environments/environment';

const localOnlyRoutes: Routes =
	environment.appEnvironment === 'local'
		? [
				{
					path: 'labs',
					loadComponent: () => import('./features/labs/labs').then((m) => m.Labs),
				},
			]
		: [];

export const routes: Routes = [
	{
		path: 'login',
		canActivate: [publicGuard],
		loadComponent: () => import('./features/login/login').then((m) => m.Login),
	},
	{
		path: 'error',
		loadComponent: () => import('./features/error/error').then((m) => m.ErrorPage),
	},
	{
		path: '',
		canActivate: [authGuard],
		canActivateChild: [authGuard],
		loadComponent: () =>
			import('./core/layouts/private-layout/private-layout').then(
				(m) => m.PrivateLayout,
			),
		children: [
			...localOnlyRoutes,
			{
				path: 'forms',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./features/forms/form-list/form-list').then((m) => m.FormList),
					},
					{
						path: 'create',
						loadComponent: () =>
							import('./features/forms/form-editor/form-editor').then(
								(m) => m.FormEditor,
							),
					},
					{
						path: ':id',
						loadComponent: () =>
							import('./features/forms/form-editor/form-editor').then(
								(m) => m.FormEditor,
							),
					},
				],
			},
			{
				path: '',
				loadComponent: () => import('./features/home/home').then((m) => m.HomePage),
			},
			{
				path: 'access-denied',
				loadComponent: () =>
					import('./features/access-denied/access-denied').then((m) => m.AccessDenied),
			},
			{
				path: 'outreach',
				loadComponent: () =>
					import('./features/outreach/campaign-list/campaign-list').then(
						(m) => m.CampaignList,
					),
			},
			{
				path: 'organizations',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./features/organizations/organization-list/organization-list').then(
								(m) => m.OrganizationList,
							),
					},
					{
						path: 'compliance',
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./features/organizations/compliance/document-list/document-list').then(
										(m) => m.DocumentList,
									),
							},
							{
								path: 'create',
								loadComponent: () =>
									import('./features/organizations/compliance/document-form/document-form').then(
										(m) => m.DocumentForm,
									),
							},
							{
								path: ':id',
								loadComponent: () =>
									import('./features/organizations/compliance/document-form/document-form').then(
										(m) => m.DocumentForm,
									),
							},
						],
					},
					{
						path: 'create',
						loadComponent: () =>
							import('./features/organizations/organization-form/organization-form').then(
								(m) => m.OrganizationForm,
							),
					},
					{
						path: ':id',
						loadComponent: () =>
							import('./features/organizations/organization-form/organization-form').then(
								(m) => m.OrganizationForm,
							),
					},
				],
			},
			{
				path: 'contacts',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./features/contacts/contact-list/contact-list').then(
								(m) => m.ContactList,
							),
					},
					{
						path: 'create',
						loadComponent: () =>
							import('./features/contacts/contact-form/contact-form').then(
								(m) => m.ContactForm,
							),
					},
					{
						path: ':id',
						loadComponent: () =>
							import('./features/contacts/contact-form/contact-form').then(
								(m) => m.ContactForm,
							),
					},
				],
			},
			{
				path: 'system',
				children: [
					{
						path: 'users',
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./features/system/users/user-list/user-list').then(
										(m) => m.UserList,
									),
							},
							{
								path: 'create',
								loadComponent: () =>
									import('./features/system/users/user-form/user-form').then(
										(m) => m.UserForm,
									),
							},
							{
								path: ':id',
								loadComponent: () =>
									import('./features/system/users/user-form/user-form').then(
										(m) => m.UserForm,
									),
							},
						],
					},
					{
						path: 'access-map',
						loadComponent: () =>
							import('./features/system/access-map/access-map').then(
								(m) => m.AccessMap,
							),
					},
				],
			},
			{
				path: 'irm',
				children: [
					{
						path: 'users',
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./features/irm/users/irm-user-list/irm-user-list').then(
										(m) => m.IrmUserList,
									),
							},
							{
								path: 'create',
								loadComponent: () =>
									import('./features/irm/users/irm-user-form/irm-user-form').then(
										(m) => m.IrmUserForm,
									),
							},
							{
								path: ':id',
								loadComponent: () =>
									import('./features/irm/users/irm-user-form/irm-user-form').then(
										(m) => m.IrmUserForm,
									),
							},
						],
					},
					{
						path: 'incidents',
						children: [
							{
								path: 'report',
								canDeactivate: [reportIncidentUploadGuard],
								loadComponent: () =>
									import('./features/irm/incidents/incident-report-form/incident-report-form').then(
										(m) => m.IncidentReportForm,
									),
							},
							{
								path: ':id',
								canDeactivate: [reportIncidentUploadGuard],
								loadComponent: () =>
									import('./features/irm/incidents/incident-report-form/incident-report-form').then(
										(m) => m.IncidentReportForm,
									),
							},
							{
								path: ':id/view',
								data: { readOnly: true },
								loadComponent: () =>
									import('./features/irm/incidents/incident-report-form/incident-report-form').then(
										(m) => m.IncidentReportForm,
									),
							},
							{
								path: '',
								loadComponent: () =>
									import('./features/irm/incidents/incident-list/incident-list').then(
										(m) => m.IncidentList,
									),
							},
						],
					},
					{
						path: 'dashboard',
						loadComponent: () =>
							import('./features/irm/dashboard/irm-dashboard').then(
								(m) => m.IrmDashboard,
							),
					},
				],
			},
			{
				path: 'reports',
				children: [
					{
						path: 'irm-reports',
						loadComponent: () =>
							import('./features/reports/irm-reports/irm-report-list/irm-report-list').then(
								(m) => m.IrmReportList,
							),
					},
					{
						path: 'dashboard',
						loadComponent: () =>
							import('./features/reports/dashboard/executive-dashboard').then(
								(m) => m.ExecutiveDashboard,
							),
					},
					{
						path: 'soc-sen',
						loadComponent: () =>
							import('./features/reports/soc-sen/soc-sen-report-list/soc-sen-report-list').then(
								(m) => m.SocSenReportList,
							),
					},
					{
						path: 'custom-reports',
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./features/reports/custom-reports/custom-report-list/custom-report-list').then(
										(m) => m.CustomReportList,
									),
							},
							{
								path: 'create',
								canDeactivate: [unsavedChangesGuard],
								loadComponent: () =>
									import('./features/reports/custom-reports/custom-report-editor/custom-report-editor').then(
										(m) => m.CustomReportEditor,
									),
							},
							{
								path: ':id',
								canDeactivate: [unsavedChangesGuard],
								loadComponent: () =>
									import('./features/reports/custom-reports/custom-report-editor/custom-report-editor').then(
										(m) => m.CustomReportEditor,
									),
							},
							{
								path: ':id/view',
								data: { readonly: true },
								loadComponent: () =>
									import('./features/reports/custom-reports/custom-report-editor/custom-report-editor').then(
										(m) => m.CustomReportEditor,
									),
							},
						],
					},
				],
			},
			{
				path: 'soc',
				children: [
					{
						path: 'dashboard',
						loadComponent: () =>
							import('./features/soc/dashboard/soc-dashboard').then(
								(m) => m.SocDashboard,
							),
					},
					{
						path: '',
						loadComponent: () =>
							import('./features/soc/soc-notification-list/soc-notification-list').then(
								(m) => m.SocNotificationList,
							),
					},
					{
						path: 'create',
						loadComponent: () =>
							import('./features/soc/soc-notification-form/soc-notification-form').then(
								(m) => m.SocNotificationForm,
							),
					},
					{
						path: ':id',
						loadComponent: () =>
							import('./features/soc/soc-notification-form/soc-notification-form').then(
								(m) => m.SocNotificationForm,
							),
					},
					{
						path: ':id/view',
						data: { readOnly: true },
						loadComponent: () =>
							import('./features/soc/soc-notification-form/soc-notification-form').then(
								(m) => m.SocNotificationForm,
							),
					},
				],
			},
		],
	},
	{
		path: '**',
		loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
	},
];
